// @ts-strict-ignore
import Form from 'antd/lib/form';
import { Content } from 'antd/lib/layout/layout';
import Space from 'antd/lib/space';
import Title from 'antd/lib/typography/Title';
import * as React from 'react';
import Text from 'antd/lib/typography/Text';
import Divider from 'antd/lib/divider';
import { Link, RouteComponentProps, useHistory, useRouteMatch } from 'react-router-dom';
import routes from '../../routes';
import Button from 'antd/lib/button';
import FormItem from 'antd/lib/form/FormItem';
import * as Cookie from 'cookie';
import { UserOutlined, QuestionCircleFilled, LockOutlined } from '@ant-design/icons';
import Input from 'antd/lib/input/Input';
import { useForm } from 'antd/lib/form/Form';
import ThirdPartySSOButtons, { ThirdPartyAuthButtonVariant } from './ThirdPartySSOButtons';
import { SignUpRequest, trySignUp } from '../../actions/user';
import { useDispatch } from 'react-redux';
import { hubSpotUserToken } from '../../helpers';
import { RedirectState } from '../../typescriptHelpers';
import * as Analytics from '../../utils/analytics';
import Tooltip from 'antd/lib/tooltip';
import { antGrey } from '../styles/globalStyles';
import Group from 'antd/lib/input/Group';
import Password from 'antd/lib/input/Password';
import { globalMessage } from '../../utils/globalMessage';
import { isMobileOnly } from 'react-device-detect';
import qs from 'query-string';
import buildDistro from '../../../assets/dashboard-build-distro.svg';
import dashboard from '../../../assets/dashboard-abstract.svg';
import TermsAndConditions from './TermsAndConditions';
import { UserUpdatable } from '@/actions/updatable';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import { STAPE_ID_COOKIE_KEY } from '@/constants';
import * as Storage from '../../helpers/localStorageHelper';

export enum SignupIntentType {
    BuildDistro = 'buildDistro',
}

const SignupContextComponent: React.FC<{ signupIntent: SignupIntentType; isMobile: boolean }> = (props) => {
    let primaryText = 'A better way to ship mobile apps';
    switch (props.signupIntent) {
        case SignupIntentType.BuildDistro:
            primaryText = 'The easiest way to share and install iOS and Android testing builds';
            break;
    }
    let secondaryText =
        'Runway is your mobile org’s new home. Level up collaboration and automation, from kickoff to rollout.';
    switch (props.signupIntent) {
        case SignupIntentType.BuildDistro:
            secondaryText = 'Build Distro by Runway is the easiest way to share and install builds for testing.';
            break;
    }

    const imageSource = props.signupIntent === SignupIntentType.BuildDistro ? buildDistro : dashboard;

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: props.isMobile ? 'center' : 'flex-start',
                width: props.isMobile ? 'auto' : 400,
                gap: 8,
            }}
        >
            <Text style={{ fontSize: 18, textAlign: props.isMobile ? 'center' : 'left' }}>{primaryText}</Text>
            <Text style={{ textAlign: props.isMobile ? 'center' : 'left' }}>{secondaryText}</Text>
            <img style={{ width: 200, marginTop: 24 }} src={imageSource}></img>
        </div>
    );
};

const SignupComponent: React.FC<RouteComponentProps> = (props: RouteComponentProps) => {
    const matchBuildDistroSignup = useRouteMatch(routes.buildDistroSignup);
    const dispatch = useDispatch();
    const [form] = useForm();
    const history = useHistory();

    const signUpLoading = useIsLoading(UserUpdatable.TRY_SIGN_UP);
    const signUpError = useError(UserUpdatable.TRY_SIGN_UP);
    const signUpSuccess = useUpdated(UserUpdatable.TRY_SIGN_UP);

    const redirectState: RedirectState = props.location.state;
    const redirectPath = redirectState?.fromLocation;
    const queryParameters = props.location ? qs.parse(props.location.search) : {};
    const intentParam = queryParameters['intent'];

    let signupIntent = null;
    if (matchBuildDistroSignup) {
        signupIntent = SignupIntentType.BuildDistro;
    } else if (typeof intentParam === 'string') {
        signupIntent = intentParam as SignupIntentType;
    }

    const layout = {
        labelCol: { span: 8 },
        wrapperCol: { span: 24 },
    };

    const cookies = Cookie.parse(document.cookie);

    // MCP access requires an existing account on an Enterprise plan.
    const authRedirectRouteParam = queryParameters.authRedirectRoute;
    const pendingAuthRedirectRoute =
        (typeof authRedirectRouteParam === 'string' && authRedirectRouteParam) || Storage.getAuthRedirectRoute();
    const isMCPFlow = Storage.isMCPAuthRedirectRoute(pendingAuthRedirectRoute);

    const onFinish = (values) => {
        const signUpRequest: SignUpRequest = {
            firstName: values.firstName,
            lastName: values.lastName,
            email: values.email,
            password: values.password,
            hubSpotUserToken: hubSpotUserToken(document),
            intent: signupIntent,
            stapeId: cookies[STAPE_ID_COOKIE_KEY],
            mcpFlow: isMCPFlow ? true : undefined,
        };

        dispatch(trySignUp(signUpRequest));
    };

    React.useEffect(() => {
        if (signUpError) {
            globalMessage.error(signUpError);
        }
    }, [signUpError]);

    React.useEffect(() => {
        if (signUpSuccess) {
            const redirectTo = redirectPath ?? routes.dashboard;

            history.replace(redirectTo);
        }
    }, [signUpSuccess]);

    React.useEffect(() => {
        const properties = Analytics.defaultProperties();

        Analytics.track(Analytics.Event.ViewSignUp, properties);
    }, []);

    const mainSignUpContent = (isBuildDistro: boolean) => {
        const prompt = isBuildDistro ? 'Get started with Build Distro by Runway' : 'Get started with Runway';

        return (
            <>
                <Title
                    style={{
                        fontSize: 20,
                        textAlign: 'center',
                        marginBottom: 16,
                    }}
                >
                    {prompt}
                </Title>
                <Text type="secondary">
                    Already have an account? <Link to={routes.login}>Sign in</Link>
                </Text>
                <Space direction="vertical" size={16} style={{ marginTop: 28 }}>
                    <ThirdPartySSOButtons
                        {...props}
                        variant={ThirdPartyAuthButtonVariant.Signup}
                        intent={signupIntent}
                    />
                    <Divider>
                        <Text type="secondary">or</Text>
                    </Divider>
                    <Form
                        layout="vertical"
                        onFinish={onFinish}
                        {...layout}
                        style={{ width: 300 }}
                        form={form}
                        id="signup-email-password"
                        className="runway-form-email-password"
                    >
                        <Group>
                            <FormItem
                                name="firstName"
                                rules={[{ required: true, message: 'Please enter your first name.' }]}
                                style={{ width: 'calc(50% - 4px)', display: 'inline-block', marginRight: 4 }}
                            >
                                <Input placeholder="First name" />
                            </FormItem>
                            <FormItem
                                name="lastName"
                                rules={[{ required: true, message: 'Please enter your last name.' }]}
                                style={{ width: 'calc(50% - 4px)', display: 'inline-block', marginLeft: 4 }}
                            >
                                <Input placeholder="Last name" />
                            </FormItem>
                        </Group>
                        <FormItem name="email" rules={[{ required: true, message: 'Please enter an email' }]}>
                            <Input prefix={<UserOutlined className="site-form-item-icon" />} placeholder="Work email" />
                        </FormItem>
                        <FormItem>
                            <div style={{ width: '100%' }}>
                                <FormItem
                                    name="password"
                                    rules={[
                                        { required: true, message: 'Please enter a password' },
                                        { min: 12, message: 'Password must be at least 12 characters long.' },
                                        // Every rule runs even when the field is empty, so each validator has to
                                        // tolerate an undefined value rather than calling String methods on it.
                                        {
                                            validator: (rule, value: string | undefined) =>
                                                /[A-Z]/.test(value ?? '') ?
                                                    Promise.resolve()
                                                :   Promise.reject(
                                                        new Error(
                                                            'Password must contain at least one uppercase letter.',
                                                        ),
                                                    ),
                                        },
                                        {
                                            validator: (rule, value: string | undefined) =>
                                                /[0-9]/.test(value ?? '') ?
                                                    Promise.resolve()
                                                :   Promise.reject(
                                                        new Error('Password must contain at least one number.'),
                                                    ),
                                        },
                                        {
                                            validator: (rule, value: string | undefined) =>
                                                /[a-z]/.test(value ?? '') ?
                                                    Promise.resolve()
                                                :   Promise.reject(
                                                        new Error(
                                                            'Password must contain at least one lowercase letter.',
                                                        ),
                                                    ),
                                        },
                                        {
                                            validator: (_, value: string | undefined) =>
                                                /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(value ?? '') ?
                                                    Promise.resolve()
                                                :   Promise.reject(
                                                        new Error('Password must contain at least one symbol.'),
                                                    ),
                                        },
                                    ]}
                                    noStyle
                                >
                                    <Password
                                        prefix={<LockOutlined className="site-form-item-icon" />}
                                        placeholder="Password"
                                        style={{ width: 'calc(100% - 22px)' }}
                                    />
                                </FormItem>
                                <Tooltip
                                    title={
                                        <div style={{ display: 'flex', flexDirection: 'column', padding: 8 }}>
                                            <Text style={{ color: 'white' }}>New password must contain:</Text>
                                            <ul style={{ margin: 0 }}>
                                                <li>At least 12 characters</li>
                                                <li>At least 1 number</li>
                                                <li>At least 1 lowercase letter</li>
                                                <li>At least 1 uppercase letter</li>
                                                <li>At least 1 symbol</li>
                                            </ul>
                                        </div>
                                    }
                                >
                                    <QuestionCircleFilled
                                        style={{
                                            color: antGrey.grey5,
                                            flexShrink: 1,
                                            width: 14,
                                            height: 14,
                                            marginLeft: 8,
                                        }}
                                    />
                                </Tooltip>
                            </div>
                        </FormItem>
                        <FormItem>
                            <Button type="primary" htmlType="submit" style={{ width: '100%' }} loading={signUpLoading}>
                                Sign up
                            </Button>
                        </FormItem>
                    </Form>
                    <TermsAndConditions />
                </Space>
            </>
        );
    };

    return (
        <Content
            style={{
                flexShrink: 0,
                display: 'flex',
                flexDirection: isMobileOnly ? 'column' : 'row',
                justifyContent: 'center',
                alignItems: 'center',
                gap: isMobileOnly ? 8 : 40,
                marginTop: isMobileOnly ? 24 : 0,
            }}
        >
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    width: isMobileOnly ? 'auto' : 300,
                }}
            >
                {mainSignUpContent(signupIntent === SignupIntentType.BuildDistro)}
            </div>
            <Divider type={isMobileOnly ? 'horizontal' : 'vertical'} style={{ height: isMobileOnly ? 1 : 300 }} />
            <SignupContextComponent isMobile={isMobileOnly} signupIntent={signupIntent} />
            {isMobileOnly && <Divider style={{}} />}
        </Content>
    );
};

export default SignupComponent;
